import { h, FunctionalComponent } from 'preact';
import { useCallbackRef, FocusableTrapElement, useFocusableElement } from '@uw/common-components';
import { isMobile } from 'helpers/detect-user-agent';
import { useCallback, useEffect, useMemo, useRef } from 'preact/hooks';
import { useTranslation } from 'services/use-translation';
import { headerStore, HOME_TITLE } from 'global/header.store';
import { useConnect } from 'helpers/use-connect-to-global-store';
import { stateStore } from 'global/state.store';
import './header.scss';
import { BackArrowIcon, CloseIcon } from '@uw/icons';

export const Header: FunctionalComponent<{ close: () => void }> = ({ close }) => {
  const { translate } = useTranslation();
  const [backState] = useConnect(headerStore.backState);
  const [title] = useConnect(headerStore.title);
  const showHotKey = useMemo(() => title === HOME_TITLE, [title]);
  const headerRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const [backButton, backButtonRef] = useCallbackRef<HTMLButtonElement>();

  const callbackRef = useFocusableElement(FocusableTrapElement.FirstElement, headerRef, backButton);

  useEffect(() => {
    const unsubscribe = stateStore.angularStateChanges$.subscribe(() => {
      setTimeout(() => backButton?.focus());
    });

    return () => unsubscribe();
  }, [backButton]);

  useEffect(() => {
    stateStore.onOpen$.subscribe(() => {
      closeButtonRef.current.focus();
    });
  }, []);
  

  const clickBack = useCallback(() => {
    stateStore.state.next(backState);
  }, [backState]);

  return (
    <div ref={callbackRef} class="uwaw-header">
      <div class="uwaw-header__l">
        {backState && (
          <button
            ref={backButtonRef}
            type="button"
            class="uwaw-header__back-btn"
            onClick={clickBack}
          >
            <BackArrowIcon />
            <span class="focus-outline" />
          </button>
        )}
        <h1 class="uwaw-header__title">
          {translate(title)} {!isMobile() && showHotKey && <span>(CTRL+U)</span>}
        </h1>
      </div>
      <button
        ref={closeButtonRef}
        class="uwaw-header__close"
        type="button"
        onClick={close}
        onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
        }}
        aria-label={translate('widget.header.close.aria')}
      >
        <CloseIcon height={16} />
        <span class="focus-outline" />
      </button>
    </div>
  );
};
